import { useCallback, useMemo, useState } from "react"
import { useLastIntradayBatch } from "hooks/intraday"
import { getToday } from "helpers/types/dates"
import { useAppSelector } from "hooks/redux"
import { useDashboardId } from "connected-components/dashboard/context/dashboard-id"
import { DEFAULT_CORE_STATE } from "connected-components/dashboard/store/core-slice"
import PropTypes from "prop-types"
import { useModelMetadata } from "resources/models"
import { useModelBetaFields } from "resources/beta"
import { useIsRfrAttributionEnabled } from "resources/account"
import { useClassifications } from "resources/classifications"
import { useContentSetsMetadata } from "resources/content-sets"
import { CONTENT_SET_DATA_TYPES } from "helpers/contentSetConstants"
import { METRIC_TYPES } from "helpers/metrics/constants"
import { METRIC_OPTIONS } from "helpers/metrics/definitions"
import { createMetricGroup } from "helpers/metrics/utils"
import { flattenMetrics, getContentSetMetrics } from "helpers/metrics"
import { useAssetViewDashboardMetrics } from "helpers/metrics/hooks"
import { useAvailableGroupByOptions } from "connected-components/dashboard/helpers"
import { useUpdateModule } from "connected-components/dashboard/hooks/use-update-module"
import { useGroupByIntradayCorrection } from "connected-components/asset-view/hooks/use-group-by-intraday-correction"
import ContributorsDataIntegrator from "gatherers/contributors-data-integrator.jsx"
import { DrilldownContributorsDataGrid } from "renderers/contributors-datagrid"

import { NAMED_VIEW_LABELS, createDrilldownGatherers } from "./drilldown-gatherers"
import { resolveGroupBySelection } from "./resolve-group-by-selection"
import { resolveHasNewerBatch, resolveRefreshTarget } from "./resolve-has-newer-batch"
import { resolveDrilldownInstruments } from "connected-components/dashboard/drilldown/drilldown-instrument-series"

const EMPTY_ARRAY = []

// Value-set keys that represent non-factor views (position descriptor, NMV).
// Used by filterFactorOptions to skip non-factor entries when checking exposure.
const FACTOR_PICKER_SKIP_PREFIXES = new Set(["composition", "marketValue"])

// Drilldown is read-only (no metric-display toggle) so the beta data path is always the
// standalone shape. Module-level avoids a useCallback allocation on every render.
const getBetaDrilldownDataPath = (path) => `beta.securityBeta.${path}`

// Sentinel selectedCategory values that mean "all categories" — not a real category id.
const CONTENT_SET_SENTINEL_CATEGORIES = new Set(["all", "total", "factors"])

/** Returns the human-readable label for a drilldown view id. */
function resolveViewLabel(view, contentSets) {
  if (NAMED_VIEW_LABELS[view]) return NAMED_VIEW_LABELS[view]
  return contentSets?.find(cs => cs.id === view)?.name ?? view
}

// Views that only support individual factor categories — "factors" (the aggregated sentinel
// used by performance and risk) is not meaningful here and must be stripped from drilldown seeds.
const VIEWS_WITHOUT_FACTORS_AGGREGATION = new Set([
  "exposure",
  "factor-sensitivity",
  "predictedFactorSensitivity",
  "historicalFactorSensitivity",
])

const ALWAYS_VISIBLE_METRIC_PREFIXES = new Set([
  "marketValue",
  "percentEquity",
  "description",
  "sector",
  "country",
  "averagePercentEquity",
])

// Total-contribution column id per view, used as the default sort when a performance/risk drilldown
// is opened from a summary/category graph line that carries no single factor. Absent => NMV fallback.
const TOTAL_CONTRIBUTION_SORT_ID = { performance: "totalReturn", risk: "totalDecompRisk" }

// Maps the clicked performance graph line id to the matching contributors column — so clicking
// Factor opens sorted by factorsReturn, Alpha by specificReturn, etc. rather than always totalReturn.
const PERFORMANCE_SUMMARY_SORT_ID = {
  total: "totalReturn",
  factor: "factorsReturn",
  factor_total: "factorsReturn",
  alpha: "specificReturn",
  tradingPnl: "tradingReturn",
  residual: "residualReturn",
}

/**
 * Returns whether a metric should be shown in the drilldown panel.
 * Always keeps NMV, % equity, and the standard instrument-info columns.
 * When a specific factor is selected:
 *   - Keeps that factor's metric and its dual contribution/standalone variants.
 *   - Performance and risk metric ids carry a "perf_"/"risk_" prefix to avoid
 *     MUI column-field collisions; we match both the bare id and the prefixed form.
 *   - Content-set metrics have compound prefixes ("<contentSetId>.<cat>.<factorId>
 *     [.<metricDisplay>]"); the factor id is always the third dot-segment.
 */
function isVisibleDrilldownMetric(metric, selectedFactor) {
  if (ALWAYS_VISIBLE_METRIC_PREFIXES.has(metric.prefix)) return true
  if (!selectedFactor) return true
  // Performance contribution columns (Total Return, Specific Return, etc.) are always shown
  // regardless of which factor is selected — they're summary-level, not factor-specific.
  if (metric.category?.includes("Performance Contribution")) return true

  // Exact match (exposure, factor-sensitivity — no prefix)
  if (metric.prefix === selectedFactor) return true
  // Performance / risk prefixed form
  if (metric.prefix === `perf_${selectedFactor}`) return true
  if (metric.prefix === `risk_${selectedFactor}`) return true
  // Dual-column variants (selectedMetricDisplay: null)
  if (metric.prefix === `${selectedFactor}NetContribution`) return true
  if (metric.prefix === `${selectedFactor}SecurityExposure`) return true
  if (metric.prefix === `${selectedFactor}Standalone`) return true

  // Content-set compound prefix: "<contentSetId>.<category>.<factorId>[.<metricDisplay>]"
  if (metric.prefix) {
    const segments = metric.prefix.split(".")
    if (segments.length >= 3 && segments[2] === selectedFactor) return true
  }

  return false
}

/**
 * Detail metrics always shown in every drilldown view regardless of view type.
 * `sector` and `country` are sourced from the composition gatherer (GICS via classifications).
 * `averagePercentEquity` is sourced from the performance gatherer and shows dashes in
 * point-in-time views (exposure, risk, beta) where an average-over-time is not computed.
 */
const DRILLDOWN_DETAIL_METRICS = [
  { id: "sector", category: "" },
  { id: "country", category: "" },
  { id: "averagePercentEquity", category: "" },
]

/**
 * Returns the `metricsAndCategories` array to pass to `useAssetViewDashboardMetrics`
 * for a given drilldown view.
 * selectedMetricDisplay: null triggers dual contribution + standalone columns for
 * factor-level views (exposure and factor sensitivity).
 */
function getMetricsCategories(view, selectedCategory, contentSetItems, betaMetricsOptions, selectedFactor, selectedMetricDisplay) {
  if (view === "exposure") return [...DRILLDOWN_DETAIL_METRICS, { category: "exposure", selectedCategory, selectedMetricDisplay: null }]
  if (view === "risk") {
    const riskEntry = { category: "risk", selectedCategory }
    // When drilling into a specific factor, add exposure (contribution + security exposure) after risk
    const entries = selectedFactor
      ? [riskEntry, { category: "exposure", selectedCategory, selectedMetricDisplay: null }]
      : [riskEntry]
    return [...DRILLDOWN_DETAIL_METRICS, ...entries]
  }
  if (view === "factor-sensitivity" || view === "predictedFactorSensitivity") {
    return [...DRILLDOWN_DETAIL_METRICS, { category: "factorSensitivity", factorSensitivityType: "predictedFactorSensitivity", selectedCategory, selectedMetricDisplay: null }]
  }
  if (view === "historicalFactorSensitivity") {
    return [...DRILLDOWN_DETAIL_METRICS, { category: "factorSensitivity", factorSensitivityType: "historicalFactorSensitivity", selectedCategory, selectedMetricDisplay: null }]
  }
  // getBetaMetrics reads betaFields/getBetaDataPath unconditionally — without them it throws
  // (betaFields.includes) and the whole panel error-boundaries out ("Beta won't open").
  if (view === "beta") return [...DRILLDOWN_DETAIL_METRICS, { category: "beta", ...betaMetricsOptions }]
  if (view === "performance") {
    const perfEntry = { category: "performance", selectedCategory, selectedMetricDisplay: selectedMetricDisplay ?? METRIC_TYPES.CUMULATIVE_PERCENT }
    // When drilling into a specific factor, add exposure (contribution + standalone) after performance
    const entries = selectedFactor
      ? [perfEntry, { category: "exposure", selectedCategory, selectedMetricDisplay: null }]
      : [perfEntry]
    return [...DRILLDOWN_DETAIL_METRICS, ...entries]
  }
  // No composition category: re-adding marketValue would duplicate the default without its GMV guard, and the unguarded copy is the one that survives for ETF/index.
  if (view === "composition") return DRILLDOWN_DETAIL_METRICS
  // Content-set: items are resolved in the component and passed in to satisfy ASSET_METRIC_CATEGORIES.contentSet
  return [...DRILLDOWN_DETAIL_METRICS, { category: "contentSet", items: contentSetItems ?? EMPTY_ARRAY }]
}

function getDrilldownResourceMetadataName(modelId, positionSet) {
  return `drilldownResourceMetadata-${modelId}-${positionSet?.type}-${positionSet?.id}`
}

export default function DrilldownAssetView({ moduleId, seed, frozenSnapshot }) {
  const { view, selectedFactor, selectedCategory, selectedMetricDisplay, clickedItemId } = seed
  const dashboardId = useDashboardId()
  // The dashboard's live price clock — refreshing must go here, not to the latest batch, once
  // pricing has moved past even that batch.
  const intradayPricingAsOf = useAppSelector((s) => s.dashboard.core[dashboardId]?.intradayPricingAsOf ?? DEFAULT_CORE_STATE.intradayPricingAsOf)
  const {
    modelId,
    positionSet,
    base,
    startDate,
    endDate,
    isIntradayMode,
    isEndVsStartDateComparison,
  } = frozenSnapshot

  // Whether the snapshot's date/view qualifies for intraday at all. This differs by view:
  //   - Performance is a time-series: it only shows intraday *times* for a single day (today).
  //     A multi-day range — even one ending today — is day-separated (from !== to), and the
  //     performance gatherer falls back to the standard cumulative query.
  //   - Risk/exposure/beta/content-set are point-in-time *as of* endDate; their snapshot still
  //     carries a range startDate, so they're intraday whenever endDate is today (the dashboard
  //     endDate, frozen in intraday mode).
  // isIntradayMode is the dashboard's frozen mode; we re-check endDate against today because a
  // clicked performance dot can freeze a past endDate while the dashboard was in intraday mode.
  const isIntradayView = isIntradayMode
    && endDate === getToday()
    && (view !== "performance" || startDate === endDate)

  const [refreshKey, setRefreshKey] = useState(0)
  // Refresh advances the batch id / as-of to the latest batch; a refocus (a new frozen snapshot
  // merged in while the panel stays mounted) resets back to the freshly-frozen batch. Derive the
  // active values and fall back to the snapshot when it changes, rather than re-initialising
  // useState (which only runs on mount) — mirrors the groupByState refocus pattern below.
  const frozenBatchId = frozenSnapshot.batchId ?? null
  const frozenAsOf = frozenSnapshot.intradayAsOf ?? null
  const frozenBatchDate = frozenSnapshot.batchDate ?? null
  const [refreshState, setRefreshState] = useState({
    frozen: { batchId: frozenBatchId, asOf: frozenAsOf },
    batchId: frozenBatchId,
    asOf: frozenAsOf,
    date: frozenBatchDate,
  })
  const isRefocused = refreshState.frozen.batchId !== frozenBatchId || refreshState.frozen.asOf !== frozenAsOf
  const currentBatchId = isRefocused ? frozenBatchId : refreshState.batchId
  const currentAsOf = isRefocused ? frozenAsOf : refreshState.asOf
  // The pinned batch's own calendar day — required so a stale, prior-day pin (open trades
  // blocking auto-refresh) isn't anchored to today when compared against the latest batch.
  const currentBatchDate = isRefocused ? frozenBatchDate : refreshState.date
  // Whether the data is *actually* intraday, vs. just the qualifying date — the "As of {time}"
  // label/refresh must track this. Same predicate the gatherer routes on.
  const isIntradayData = isIntradayView
  // Live-pricing intraday performance has no committed batch, so the gatherer's batchless path
  // reads contributors from today's (not-yet-run) EOD simulation and returns none. Flag this so
  // the panel can explain the empty grid rather than showing a bare skeleton.
  const intradayContributorsUnavailable = isIntradayData && view === "performance" && !currentBatchId
  // Latest batch for the FROZEN portfolio (not the dashboard's current instrument), so Refresh
  // advances to this portfolio's newest snapshot and a dashboard instrument change while the
  // panel floats can't swap in a batch for a different portfolio.
  const [latestBatch] = useLastIntradayBatch(positionSet, isIntradayView)
  // Performance refresh keeps the batch pinned (clearing it would swap the contributors grid for
  // the batchless "unavailable" state), but a pricing tick is still refreshable — the live-batch
  // intradaySimulation route prices at fetch time.
  const prefersBatchOverLivePricing = view === "performance"
  const hasNewerBatch = resolveHasNewerBatch(latestBatch, currentBatchId, currentAsOf, currentBatchDate, intradayPricingAsOf)
  const snapshotSegment = frozenSnapshot.segment ?? null
  const [groupByState, setGroupByState] = useState({ resetKey: snapshotSegment, selection: null })
  const [isExpanded, setIsExpanded] = useState(false)

  const effectiveGroupBy = resolveGroupBySelection(snapshotSegment, groupByState)

  // Data stays pinned to the clicked batch; only Refresh advances it (and the as-of label) to
  // whichever is actually the latest snapshot now — the newest batch, or live pricing if it's
  // since moved past that batch too. Re-pin the frozen baseline so this doesn't read as a refocus.
  const handleRefresh = useCallback(() => {
    setRefreshKey(prev => prev + 1)
    setRefreshState({
      frozen: { batchId: frozenBatchId, asOf: frozenAsOf },
      ...resolveRefreshTarget(latestBatch, intradayPricingAsOf, { batchId: currentBatchId, asOf: currentAsOf, date: currentBatchDate }, { preferBatch: prefersBatchOverLivePricing }),
    })
  }, [latestBatch, intradayPricingAsOf, frozenBatchId, frozenAsOf, currentBatchId, currentAsOf, currentBatchDate, prefersBatchOverLivePricing])

  const handleGroupByChange = useCallback((newGroupBy) => {
    setGroupByState({ resetKey: snapshotSegment, selection: newGroupBy })
  }, [snapshotSegment])

  const [modelMetadata] = useModelMetadata(modelId)

  // Beta needs the model's beta fields both for the metric columns (getBetaMetrics) and for
  // the gatherer's GraphQL field selection. Declared here so it's available to the gatherers memo.
  const [betaFields] = useModelBetaFields(modelId)

  const [contentSets] = useContentSetsMetadata()

  const [classifications] = useClassifications()

  const [availableGroupByOptions] = useAvailableGroupByOptions(positionSet, endDate, {
    isActive: Boolean(base),
    hasMultipleUniqueInstruments: false,
  })

  // Mirrors asset-view's grouping correction for this same stale-selection case.
  useGroupByIntradayCorrection({
    groupBy: effectiveGroupBy,
    isActive: Boolean(base),
    hasMultipleUniqueInstruments: false,
    availableGroupByOptions,
    setGroupBy: handleGroupByChange,
  })

  const isRfrAttributionEnabled = useIsRfrAttributionEnabled()

  // Declared before effectiveSeed so both memos below can use it.
  const isContentSetView = !NAMED_VIEW_LABELS[view]

  // When the graph double-clicks a *category* row (e.g. "Style" in factor-sensitivity
  // "All Factors" mode, or an "ENVIRONMENTAL" category in a content-set view), the seed's
  // selectedFactor is the category id, not an individual factor id. Detect this by matching
  // against risk-model categories OR content-set categories.
  // (Declared before effectiveSeed, which reads it.)
  const selectedFactorIsCategory = useMemo(() => {
    if (!selectedFactor) return false
    // The exposure graph always passes individual factor ids (never category ids), so a factor
    // id that happens to match a category id in the model must still be treated as a factor.
    if (view === "exposure") return false
    // When selectedCategory is a real category id (not a sentinel like "total" / "all"), the
    // user clicked a factor *within* that category — even if the factor id happens to share a
    // name with a category (e.g. "Value" is a style sub-factor in some models but also a
    // category in others). Trust the category context rather than the id collision.
    if (selectedCategory && !CONTENT_SET_SENTINEL_CATEGORIES.has(selectedCategory)) return false
    if (modelMetadata?.categories?.some(cat => cat.id === selectedFactor)) return true
    if (isContentSetView) {
      return contentSets?.find(cs => cs.id === view)?.categories?.some(cat => cat.id === selectedFactor) ?? false
    }
    return false
  }, [selectedFactor, selectedCategory, modelMetadata, isContentSetView, contentSets, view])

  // For factor-level drilldowns, look up which category the selected factor belongs to.
  // Used both to resolve the gatherers' category (effectiveSeed, below) and so
  // getMetricsCategories can request all metrics in that category. Content-set factors are
  // not in the risk-model metadata, so we also check the content-set metadata when the view
  // id is a content-set id.
  const selectedFactorCategory = useMemo(() => {
    if (!selectedFactor) return undefined
    // Risk-model factors
    const riskFactor = modelMetadata?.factors?.find(factor => factor.id === selectedFactor)
    if (riskFactor) return riskFactor.category
    // Content-set factors — look inside the specific content set for this drilldown view
    const contentSetFactor = contentSets
      ?.find(cs => cs.id === view)
      ?.factors?.find(factor => factor.id === selectedFactor)
    return contentSetFactor?.category
  }, [selectedFactor, modelMetadata, contentSets, view])

  // Build an effective seed for the gatherers.
  // Two cases:
  //   1. selectedFactor is actually a category id → swap to selectedCategory so the API
  //      receives the right filter.
  //   2. A specific selectedFactor with a sentinel selectedCategory ("all"/"total"/"factors")
  //      → resolve selectedCategory to the factor's actual category so the gatherers fetch and
  //      store under the same category the columns are built for (resolvedDrilldownCategory).
  //      Without this, e.g. exposure/factor-sensitivity drilldowns opened from an "All Factors"
  //      view store data under the sentinel category and every cell renders blank.
  const effectiveSeed = useMemo(() => {
    if (selectedFactorIsCategory) {
      return { ...seed, selectedFactor: undefined, selectedCategory: selectedFactor }
    }
    if (selectedFactor && selectedFactorCategory
      && (!selectedCategory || CONTENT_SET_SENTINEL_CATEGORIES.has(selectedCategory))) {
      return { ...seed, selectedCategory: selectedFactorCategory }
    }
    return seed
  }, [seed, selectedFactorIsCategory, selectedFactor, selectedFactorCategory, selectedCategory])

  // null means "don't collapse to one metric type" — shared between the gatherer and the column
  // definitions: getContributorFactorValues (in the content-set gatherer) keeps the full factor
  // object instead of reducing to a single scalar, and getContentSetMetrics builds one column per
  // non-disabled metric type (netContribution / currencyExposure / securityExposure) instead of
  // one. They must agree on null-vs-a-concrete-id or the column dataPath resolves to an object
  // (blank cells).
  const contentSetMetricDisplay = isContentSetView ? null : undefined

  const gatherers = useMemo(
    () => createDrilldownGatherers(view, frozenSnapshot, effectiveSeed, {
      refreshKey,
      groupBy: effectiveGroupBy,
      classifications,
      isRfrAttributionEnabled,
      betaFields,
      contentSetMetricDisplay,
      currentIntradayBatchId: currentBatchId,
      // Live latest id, so the performance gatherer routes a still-latest frozen batch the same
      // way the source panel does (and treats a superseded one as genuinely pinned).
      lastIntradayBatchId: latestBatch?.id ?? null,
      isIntradayView,
    }),
    [view, frozenSnapshot, effectiveSeed, refreshKey, effectiveGroupBy, classifications, isRfrAttributionEnabled, betaFields, contentSetMetricDisplay, currentBatchId, latestBatch?.id, isIntradayView],
  )

  // When a category id was double-clicked (e.g. "Style" row in factors-view "All Factors"):
  // For performance/risk, effectiveSelectedFactor stays as the category id so
  // isVisibleDrilldownMetric can match the aggregate column (perf_style / risk_style).
  // For exposure, there is no category-aggregate column — individual factor columns carry
  // individual factor ids. Pass undefined so all factors in the category are shown.
  // resolvedDrilldownCategory must NOT be set to "style" — that would cause getMetricsCategories
  // to return individual style-factor columns. It falls through to seed.selectedCategory
  // (typically undefined from the factors-view), triggering the "all categories aggregated" branch.
  const effectiveSelectedFactor = (selectedFactorIsCategory && view === "exposure")
    ? undefined
    : selectedFactor

  // Factor picker — sibling factors in the same category. Declared after selectedFactorCategory
  // so both are in scope. Switching updates selectedFactor in the seed; the underlying query
  // is unchanged (same cache key) so switching is instant.
  const updateModule = useUpdateModule()
  const factorOptions = useMemo(() => {
    if (!effectiveSelectedFactor || !selectedFactorCategory) return []
    const modelFactors = (modelMetadata?.factors || []).filter(f => f.category === selectedFactorCategory)
    const csFactors = isContentSetView
      ? (contentSets?.find(cs => cs.id === view)?.factors || []).filter(f => f.category === selectedFactorCategory)
      : []
    const factors = isContentSetView ? csFactors : modelFactors
    return factors.map(f => ({ id: f.id, primaryText: f.label || f.name || f.id }))
  }, [effectiveSelectedFactor, selectedFactorCategory, modelMetadata, contentSets, view, isContentSetView])

  const handleFactorChange = useCallback((newFactorId) => {
    if (!moduleId || newFactorId === effectiveSelectedFactor) return
    updateModule(moduleId, {
      config: { drilldownSeed: { ...seed, selectedFactor: newFactorId } },
    })
  }, [moduleId, effectiveSelectedFactor, seed, updateModule])

  // Passed to the renderer so it can prune the factor picker to options with actual exposure
  // in the loaded data. Lives here (not in the renderer) because the skip-prefix set is
  // gatherer-shape knowledge — the renderer should stay unaware of value-set naming conventions.
  const filterFactorOptions = useCallback((options, data) => {
    if (!options?.length) return options
    const totalValues = data?.values
    if (!totalValues) return options // data not loaded yet — show all

    const viewValueSets = Object.entries(totalValues)
      .filter(([k]) => !FACTOR_PICKER_SKIP_PREFIXES.has(k))
      .map(([, v]) => v)
      .filter(v => v && typeof v === "object")

    if (!viewValueSets.length) return options

    // Check whether the data contains values for multiple siblings (unfiltered) or just one.
    // ≤1 sibling means the data was already filtered to a single factor — can't assess siblings.
    const siblingIdSet = new Set(options.map(o => o.id))
    const siblingKeysInData = viewValueSets.flatMap(vals => Object.keys(vals).filter(k => siblingIdSet.has(k)))
    const uniqueSiblingCount = new Set(siblingKeysInData).size
    if (uniqueSiblingCount <= 1) return options

    const hasExposure = (id) =>
      viewValueSets.some(vals => { const v = vals[id]; return v !== null && v !== undefined && v !== 0 })
      || Object.values(data.children || {}).some(child =>
        Object.entries(child?.values || {})
          .filter(([k]) => !FACTOR_PICKER_SKIP_PREFIXES.has(k))
          .some(([, vals]) => { const v = vals?.[id]; return v !== null && v !== undefined && v !== 0 }),
      )

    // Always keep the currently displayed factor even if its reading is zero —
    // removing it would make it impossible to navigate away to a sibling.
    return options.filter(({ id }) => id === effectiveSelectedFactor || hasExposure(id))
  }, [effectiveSelectedFactor])

  // Defer metric category definitions until modelMetadata is loaded — several category
  // builder functions (e.g. getPerformanceMetrics) read modelMetadata.factors unconditionally.
  // Falls back to the factor's parent category when a specific factor was clicked.
  //
  // These are all "show every category" sentinels, NOT real category ids, so they must be
  // stripped — otherwise getContentSetMetrics/getMetricsByCategory filter by a category that
  // matches nothing and the drilldown comes up empty:
  //   - "factors": performance/risk all-factors view
  //   - "total":   factor-sensitivity / content-set graph all view
  //   - "all":     content-set factors view all-categories selection
  // When stripped, resolvedDrilldownCategory falls through to the clicked factor's own category
  // (e.g. an MSCI ESG "SOCIAL" factor), which getContentSetMetrics can actually match.
  const baseCategory = (() => {
    const cat = selectedCategory
    if (cat === "factors" && (VIEWS_WITHOUT_FACTORS_AGGREGATION.has(view) || isContentSetView)) return undefined
    if ((cat === "total" || cat === "all") && (VIEWS_WITHOUT_FACTORS_AGGREGATION.has(view) || isContentSetView)) return undefined
    return cat
  })()
  const resolvedDrilldownCategory = baseCategory || selectedFactorCategory

  // For content-set views, resolve the flat metric items ahead of time.
  // ASSET_METRIC_CATEGORIES.contentSet expects `metrics.items` to be populated, but the drilldown
  // only has the content set ID — not the pre-flattened metric list that the regular template
  // receives via flattenedMetrics. We build it here from the loaded content-set metadata.
  const contentSetItems = useMemo(() => {
    if (!isContentSetView || !contentSets || !resolvedDrilldownCategory) return null
    const contentSet = contentSets.find(cs => cs.id === view)
    if (!contentSet) return null
    const metricOptions = contentSet.dataType === CONTENT_SET_DATA_TYPES.SENSITIVITY
      ? METRIC_OPTIONS.sensitivityContentSet
      : METRIC_OPTIONS.contentSet
    const filteredMetricOptions = metricOptions.filter(option => !option.disabled)
    const metricGroup = createMetricGroup(contentSet.id, contentSet, {
      metricOptions: filteredMetricOptions,
      name: contentSet.name,
    })
    if (!metricGroup) return null
    const flattenedForContentSet = flattenMetrics([metricGroup])
    // contentSetMetricDisplay is null here — one column per non-disabled metric type, each with its
    // own suffixed label (getContentSetMetrics), matching the gatherer's un-reduced factor object.
    return getContentSetMetrics(view, flattenedForContentSet, resolvedDrilldownCategory, contentSetMetricDisplay)
  }, [view, contentSets, resolvedDrilldownCategory, contentSetMetricDisplay, isContentSetView])

  // Beta metrics need betaFields (declared above) + a data-path resolver, else getBetaMetrics
  // throws on betaFields.includes(...).
  const betaMetricsOptions = useMemo(() => ({ betaFields, getBetaDataPath: getBetaDrilldownDataPath }), [betaFields])

  const metricsAndCategories = useMemo(
    () => (modelMetadata ? getMetricsCategories(view, resolvedDrilldownCategory, contentSetItems, betaMetricsOptions, effectiveSelectedFactor, selectedMetricDisplay) : []),
    [modelMetadata, view, resolvedDrilldownCategory, contentSetItems, betaMetricsOptions, effectiveSelectedFactor, selectedMetricDisplay],
  )

  // Prepend the description (name) column to match the summary template's display
  const prependMetrics = useMemo(() => [{ id: "description" }], [])

  // Resolve which second column (if any) to render alongside the original, derived from the
  // frozen snapshot's instruments: a comparison/optimization target, a rebalanced portfolio, or —
  // under end-vs-start — the same portfolio as of the start date. The column labels (O/E, R, C/T/S)
  // and data-path suffixes come from the asset-view metrics hook's existing instrument slots.
  const { instrumentColumns, rebalancedInstrument, comparisonInstrument: resolvedComparisonInstrument, instrumentsWithMetadata } = useMemo(
    () => resolveDrilldownInstruments(frozenSnapshot),
    [frozenSnapshot],
  )

  const allMetrics = useAssetViewDashboardMetrics(
    metricsAndCategories,
    positionSet,
    rebalancedInstrument,
    resolvedComparisonInstrument,
    {
      instrumentColumns,
      isActive: !!base,
      modelMetadata,
      instrumentsWithMetadata,
      setTradedPosition: null,
      isExpanded,
      disableActiveTrading: true,
      isRfrAttributionEnabled,
      prependMetrics,
      isEndVsStartDateComparison,
    },
  )

  const metrics = useMemo(() => {
    // Show description (name) instead of bloomberg ticker in the drilldown header columns
    const baseMetrics = allMetrics.filter(metric => metric.prefix !== "bloombergTicker")
    const visibleMetrics = effectiveSelectedFactor
      ? baseMetrics.filter(metric => isVisibleDrilldownMetric(metric, effectiveSelectedFactor))
      : baseMetrics

    // Deduplicate by id — DRILLDOWN_DETAIL_METRICS entries (sector, country) share an id
    // with category-generated columns (e.g. the "sector" exposure/risk factor column).
    // Keep the first occurrence so the GICS classification column wins.
    const seenIds = new Set()
    const deduplicated = visibleMetrics.filter(metric => {
      if (seenIds.has(metric.id)) return false
      seenIds.add(metric.id)
      return true
    })

    // Column order matches the regular asset view (descriptors first, then view-specific).
    // The drilldown panel scrolls to the first view-specific column on open (see drilldown-panel).
    return deduplicated
  }, [allMetrics, effectiveSelectedFactor])

  // Default sort. When a specific factor is selected, sort descending by magnitude on that factor's
  // primary column — derived from the actual computed columns rather than string-built, since the
  // real column id is the only source of truth (content-set ids embed a category segment whose
  // casing can silently mismatch). Skip descriptor and performance-contribution summary columns so
  // we land on the selected factor's metric. A risk category click resolves its risk_<cat> column
  // here too. Otherwise — a total/summary click with no single factor — sort on the view's total
  // contribution column so the table opens ordered by what you drilled into, not position size.
  const defaultSort = useMemo(() => {
    if (effectiveSelectedFactor) {
      const factorColumn = metrics.find(metric =>
        metric.type !== "comparison"
        && metric.type !== "delta"
        && !ALWAYS_VISIBLE_METRIC_PREFIXES.has(metric.prefix)
        && !metric.category?.includes("Performance Contribution")
        && isVisibleDrilldownMetric(metric, effectiveSelectedFactor),
      )
      if (factorColumn) return { path: factorColumn.id, direction: "desc_abs" }
    }
    // When a specific performance summary line was clicked (Total/Factor/Alpha/Trading/Residual),
    // sort by that line's matching column rather than always defaulting to totalReturn.
    if (view === "performance" && clickedItemId) {
      const summarySort = PERFORMANCE_SUMMARY_SORT_ID[clickedItemId]
      if (summarySort && metrics.some(metric => metric.id === summarySort)) {
        return { path: summarySort, direction: "desc_abs" }
      }
    }
    const totalContributionId = TOTAL_CONTRIBUTION_SORT_ID[view]
    if (totalContributionId && metrics.some(metric => metric.id === totalContributionId)) {
      return { path: totalContributionId, direction: "desc_abs" }
    }
    // Composition is a concentration breakdown — open sorted by position weight, not market value.
    if (view === "composition") return { path: "percentEquity", direction: "desc_abs" }
    return { path: "marketValue", direction: "desc" }
  }, [view, effectiveSelectedFactor, metrics, clickedItemId])

  return (
    <ContributorsDataIntegrator
      modelId={modelId}
      mainInstrument={positionSet}
      resourceMetadataName={getDrilldownResourceMetadataName(modelId, positionSet)}
      gatherers={gatherers}
      metrics={metrics}
      Renderer={DrilldownContributorsDataGrid}
      rendererProps={{
        viewLabel: resolveViewLabel(view, contentSets),
        currentFactor: effectiveSelectedFactor,
        factorOptions,
        filterFactorOptions,
        onFactorChange: handleFactorChange,
        frozenSnapshot,
        selectedFactor,
        selectedCategory,
        isIntraday: isIntradayData,
        intradayAsOf: currentAsOf,
        hasNewerBatch,
        intradayContributorsUnavailable,
        onRefresh: handleRefresh,
        defaultSort,
        groupBy: effectiveGroupBy,
        onGroupByChange: handleGroupByChange,
        availableGroupByOptions: availableGroupByOptions ?? EMPTY_ARRAY,
        isExpanded,
        onExpansionToggle: setIsExpanded,
      }}
    />
  )
}

DrilldownAssetView.propTypes = {
  moduleId: PropTypes.string,
  seed: PropTypes.shape({
    view: PropTypes.string.isRequired,
    selectedFactor: PropTypes.string,
    selectedCategory: PropTypes.string,
    selectedMetricDisplay: PropTypes.string,
    sourcePanelId: PropTypes.string.isRequired,
    clickedItemId: PropTypes.string,
  }).isRequired,
  frozenSnapshot: PropTypes.shape({
    modelId: PropTypes.string.isRequired,
    positionSet: PropTypes.object,
    base: PropTypes.object,
    startDate: PropTypes.string.isRequired,
    endDate: PropTypes.string.isRequired,
    isIntradayMode: PropTypes.bool,
    isEndVsStartDateComparison: PropTypes.bool,
    intradayAsOf: PropTypes.string,
    batchId: PropTypes.string,
    batchDate: PropTypes.string,
    segment: PropTypes.object,
    comparisonInstrument: PropTypes.object,
    secondInstrument: PropTypes.object,
  }).isRequired,
}
